/**
 * Merchant Developer Portal — iteration 34b
 * Lists API keys + outbound webhooks. Secrets are shown ONCE on creation via
 * a reveal-once modal with copy-to-clipboard. Feature-gated by
 * FEATURE_DEVELOPER_PORTAL — page shows a friendly notice if off.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Trash2, Key, Webhook as WebhookIcon, Plus, EyeOff, ShieldAlert, Check, ChevronDown, ChevronRight, RefreshCw, RotateCw } from "lucide-react";

const SCOPES = ["conversions:write", "conversions:read", "coupons:read", "campaigns:read", "analytics:read", "webhooks:manage"];
const EVENT_TYPES = ["COUPON_CLICKED", "MERCHANT_VISIT", "CONVERSION_RECEIVED", "CONVERSION_CONFIRMED", "CONVERSION_VOIDED", "CAMPAIGN_STARTED", "CAMPAIGN_ENDED", "COUPON_PUBLISHED", "COUPON_EXPIRED"];

function CopyButton({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-100"
      data-testid={testid}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function DeliveryStatusBadge({ delivery }) {
  const status = delivery.status || (delivery.status_code >= 200 && delivery.status_code < 300 ? "ok" : "fail");
  const tone = status === "ok" || status === "delivered" || status === "success"
    ? "bg-emerald-100 text-emerald-700"
    : status === "pending" || status === "retrying"
      ? "bg-amber-100 text-amber-700"
      : "bg-rose-100 text-rose-700";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${tone}`}>{delivery.status_code ?? status}</span>;
}

function isFailedDelivery(d) {
  if (d.status === "dropped" || d.status === "failed") return true;
  if (d.status === "delivered") return false;
  if (d.status_code != null && (d.status_code < 200 || d.status_code >= 300)) return true;
  return false;
}

function WebhookRow({ hook, onDelete }) {
  const [open, setOpen] = useState(false);
  const [deliveries, setDeliveries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [bulkRetrying, setBulkRetrying] = useState(false);

  async function loadDeliveries() {
    setLoading(true);
    try {
      const { data } = await api.get(`/dev/webhooks/${hook.id}/deliveries?limit=20`);
      setDeliveries(data.items || []);
    } catch (e) {
      toast.error("Failed to load delivery history");
      setDeliveries([]);
    } finally { setLoading(false); }
  }

  async function retryDelivery(d) {
    setRetryingId(d.id);
    try {
      await api.post(`/dev/webhooks/${hook.id}/deliveries/${d.id}/retry`);
      toast.success("Re-queued — the worker will retry in a few seconds.");
      setDeliveries(cur => (cur || []).map(x => x.id === d.id ? { ...x, status: "pending" } : x));
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Retry failed"));
    } finally { setRetryingId(null); }
  }

  async function retryAllFailed() {
    setBulkRetrying(true);
    try {
      const { data } = await api.post(`/dev/webhooks/${hook.id}/deliveries/retry-failed`);
      if (data.requeued > 0) {
        toast.success(`Re-queued ${data.requeued} failed ${data.requeued === 1 ? "delivery" : "deliveries"}.`);
        setDeliveries(cur => (cur || []).map(x => isFailedDelivery(x) ? { ...x, status: "pending" } : x));
      } else {
        toast.info("Nothing to retry — no failed deliveries on this webhook.");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Bulk retry failed"));
    } finally { setBulkRetrying(false); }
  }

  const failedCount = (deliveries || []).filter(isFailedDelivery).length;

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && deliveries === null) loadDeliveries();
  }

  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200" data-testid={`dev-hook-row-${hook.id}`}>
      <div className="flex items-center justify-between px-4 py-3">
        <button type="button" onClick={toggle} className="flex-1 text-left flex items-start gap-2" data-testid={`dev-hook-expand-${hook.id}`}>
          {open ? <ChevronDown className="w-4 h-4 text-slate-400 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-slate-400 mt-0.5" />}
          <div>
            <div className="text-sm font-mono text-slate-900 truncate max-w-md">{hook.url}</div>
            <div className="text-xs text-slate-500">{hook.event_types.join(", ")}</div>
            <div className="text-[10px] text-slate-400 mt-1">Created {new Date(hook.created_at).toLocaleDateString()} · {hook.active ? <span className="text-emerald-600">active</span> : <span className="text-slate-500">paused</span>}</div>
          </div>
        </button>
        <button onClick={onDelete} className="text-rose-600 hover:text-rose-700 text-xs inline-flex items-center gap-1" data-testid={`dev-hook-delete-${hook.id}`}>
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-200 px-4 py-3 bg-white" data-testid={`dev-hook-deliveries-${hook.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Recent deliveries</div>
            <div className="flex items-center gap-2">
              {failedCount > 0 && (
                <button onClick={retryAllFailed} disabled={bulkRetrying}
                        className="text-xs text-rose-700 hover:text-rose-800 inline-flex items-center gap-1 disabled:opacity-50 font-semibold"
                        data-testid={`dev-hook-retry-all-${hook.id}`}
                        title={`Requeue ${failedCount} failed ${failedCount === 1 ? "delivery" : "deliveries"}`}>
                  <RotateCw className={`w-3 h-3 ${bulkRetrying ? "animate-spin" : ""}`} />
                  {bulkRetrying ? "Retrying…" : `Retry all failed (${failedCount})`}
                </button>
              )}
              <button onClick={loadDeliveries} disabled={loading}
                      className="text-xs text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`dev-hook-refresh-${hook.id}`}>
                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
          </div>
          {loading && <div className="text-xs text-slate-400 italic py-3">Loading…</div>}
          {!loading && deliveries !== null && deliveries.length === 0 && (
            <div className="text-xs text-slate-400 italic py-3" data-testid={`dev-hook-deliveries-empty-${hook.id}`}>
              No deliveries yet — this endpoint hasn't received any events.
            </div>
          )}
          {!loading && deliveries && deliveries.length > 0 && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {deliveries.map((d, i) => {
                const failed = isFailedDelivery(d);
                const isRetrying = retryingId === d.id;
                return (
                  <div key={d.id || i} className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded bg-slate-50 border border-slate-200"
                       data-testid={`dev-hook-delivery-${hook.id}-${i}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <DeliveryStatusBadge delivery={d} />
                      <span className="font-mono text-slate-700 truncate">{d.event_type || "—"}</span>
                      {d.attempt != null && <span className="text-slate-400">· try #{d.attempt}</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className="text-slate-400">
                        {d.created_at ? new Date(d.created_at).toLocaleString() : ""}
                      </span>
                      {failed && d.id && (
                        <button
                          onClick={() => retryDelivery(d)}
                          disabled={isRetrying}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-[10px] font-semibold disabled:opacity-50"
                          data-testid={`dev-hook-delivery-retry-${hook.id}-${i}`}
                          title="Re-queue this delivery"
                        >
                          <RotateCw className={`w-3 h-3 ${isRetrying ? "animate-spin" : ""}`} />
                          {isRetrying ? "…" : "Retry"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-3 text-[10px] text-slate-400">
            Verify inbound requests using HMAC-SHA256 of the raw body with your webhook secret,
            sent in the <code className="font-mono text-slate-500">X-Cyannect-Signature</code> header.
          </div>
        </div>
      )}
    </div>
  );
}

function RevealOnceModal({ title, secret, warning, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4" data-testid={testid}>
      <div className="w-full max-w-lg rounded-2xl bg-slate-900 border border-cyan-500/30 p-6 shadow-2xl">
        <div className="inline-flex items-center gap-2 text-cyan-400 text-[10px] uppercase tracking-widest font-semibold mb-2">
          <ShieldAlert className="w-3.5 h-3.5" /> {title}
        </div>
        <h3 className="text-slate-100 text-lg font-medium mb-3">Save this now — it will not be shown again.</h3>
        <div className="rounded-lg bg-slate-950 border border-slate-700 p-3 font-mono text-xs text-cyan-300 break-all mb-3" data-testid={`${testid}-secret`}>
          {secret}
        </div>
        <div className="flex items-center justify-between">
          <CopyButton text={secret} testid={`${testid}-copy`} />
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold" data-testid={`${testid}-done`}>
            I saved it — close
          </button>
        </div>
        {warning && <p className="text-[11px] text-rose-300/80 mt-3">{warning}</p>}
      </div>
    </div>
  );
}

export default function DeveloperPortal() {
  const [enabled, setEnabled] = useState(true);
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [newKey, setNewKey] = useState({ label: "", scopes: ["conversions:write", "conversions:read"] });
  const [newHook, setNewHook] = useState({ url: "", event_types: ["CONVERSION_CONFIRMED"] });
  const [reveal, setReveal] = useState(null); // {title, secret, warning}
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const [k, h] = await Promise.all([api.get("/dev/api-keys"), api.get("/dev/webhooks")]);
      setKeys(k.data.items || []);
      setHooks(h.data.items || []);
      setEnabled(true);
    } catch (e) {
      if (e.response?.status === 404) setEnabled(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function createKey(e) {
    e.preventDefault();
    const trimmed = (newKey.label || "").trim();
    if (!trimmed) {
      toast.error("Please enter a label.");
      return;
    }
    const dup = keys.some(
      (k) => !k.revoked_at && (k.label || "").trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (dup) {
      toast.error(`A key named “${trimmed}” already exists. Pick a different name or revoke the existing one first.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/dev/api-keys", { ...newKey, label: trimmed });
      setReveal({ title: "API key created", secret: data.secret, warning: `Prefix: ${data.prefix}` });
      setNewKey({ label: "", scopes: ["conversions:write", "conversions:read"] });
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message;
      toast.error(msg || "Failed to create key");
    } finally { setBusy(false); }
  }

  async function revokeKey(id) {
    if (!window.confirm("Revoke this key immediately? Any integration using it will start failing.")) return;
    await api.delete(`/dev/api-keys/${id}`);
    toast.success("Key revoked");
    refresh();
  }

  async function createHook(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/dev/webhooks", newHook);
      setReveal({
        title: "Webhook created",
        secret: data.hmac_secret,
        warning: `Endpoint: ${data.url} · Verify incoming requests with HMAC-SHA256 using this secret.`,
      });
      setNewHook({ url: "", event_types: ["CONVERSION_CONFIRMED"] });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail?.error || "Failed to create webhook");
    } finally { setBusy(false); }
  }

  async function deleteHook(id) {
    if (!window.confirm("Delete this webhook endpoint?")) return;
    await api.delete(`/dev/webhooks/${id}`);
    toast.success("Webhook deleted");
    refresh();
  }

  function toggleScope(s) {
    setNewKey(k => ({ ...k, scopes: k.scopes.includes(s) ? k.scopes.filter(x => x !== s) : [...k.scopes, s] }));
  }
  function toggleEvent(t) {
    setNewHook(h => ({ ...h, event_types: h.event_types.includes(t) ? h.event_types.filter(x => x !== t) : [...h.event_types, t] }));
  }

  if (!enabled) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="dev-portal-disabled">
        <div className="inline-flex items-center gap-2 text-cyan-700 text-[10px] uppercase tracking-widest font-semibold mb-2">
          <ShieldAlert className="w-3.5 h-3.5" /> Developer portal
        </div>
        <h2 className="text-2xl font-display text-slate-900">Coming soon</h2>
        <p className="text-sm text-slate-500 mt-2">
          API keys + outbound webhooks aren't yet enabled on your account. Talk to your Cyannect success manager to switch it on.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="dev-portal-page">
      <header>
        <div className="inline-flex items-center gap-2 text-cyan-700 text-[10px] uppercase tracking-widest font-semibold mb-1">
          <Key className="w-3.5 h-3.5" /> Developer portal
        </div>
        <h1 className="text-3xl font-display text-slate-900">API keys &amp; webhooks</h1>
        <p className="text-sm text-slate-500 mt-1">Manage server-to-server credentials for the Cyannect Conversion API + outbound event webhooks.</p>
      </header>

      {/* API keys */}
      <section className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="dev-keys-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg text-slate-900 flex items-center gap-2"><Key className="w-4 h-4 text-cyan-600" /> API Keys</h2>
        </div>

        <form onSubmit={createKey} className="mb-6 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="dev-keys-create-form">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Label</label>
              <input
                required
                value={newKey.label}
                onChange={(e) => setNewKey({ ...newKey, label: e.target.value })}
                placeholder="e.g. Production checkout"
                aria-invalid={
                  newKey.label.trim() &&
                  keys.some((k) => !k.revoked_at && (k.label || "").trim().toLowerCase() === newKey.label.trim().toLowerCase())
                    ? true
                    : undefined
                }
                className={`w-full px-3 py-2 text-sm rounded-lg border bg-white focus:border-cyan-500 outline-none ${
                  newKey.label.trim() &&
                  keys.some((k) => !k.revoked_at && (k.label || "").trim().toLowerCase() === newKey.label.trim().toLowerCase())
                    ? "border-red-400 ring-2 ring-red-300"
                    : "border-slate-300"
                }`}
                data-testid="dev-key-label-input"
              />
              {newKey.label.trim() &&
                keys.some((k) => !k.revoked_at && (k.label || "").trim().toLowerCase() === newKey.label.trim().toLowerCase()) && (
                <div className="text-xs text-red-600 mt-1.5" data-testid="dev-key-label-error">
                  A key named “{newKey.label.trim()}” already exists. Pick a different name or revoke the existing one first.
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={
                busy ||
                newKey.scopes.length === 0 ||
                !newKey.label.trim() ||
                keys.some((k) => !k.revoked_at && (k.label || "").trim().toLowerCase() === newKey.label.trim().toLowerCase())
              }
              className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="dev-key-create-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Create key
            </button>
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Scopes</div>
            <div className="flex flex-wrap gap-2">
              {SCOPES.map(s => (
                <button type="button" key={s} onClick={() => toggleScope(s)}
                        className={`text-[11px] px-2.5 py-1 rounded-full font-mono transition ${newKey.scopes.includes(s) ? "bg-cyan-500 text-slate-900" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}
                        data-testid={`dev-key-scope-${s.replace(":", "-")}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </form>

        <div className="space-y-2" data-testid="dev-keys-list">
          {keys.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">No API keys yet — create your first above.</div>}
          {keys.map(k => (
            <div key={k.id} className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 border border-slate-200" data-testid={`dev-key-row-${k.id}`}>
              <div>
                <div className="text-sm font-medium text-slate-900">{k.label}</div>
                <div className="text-xs text-slate-500 font-mono">{k.prefix}… · scopes: {k.scopes.join(", ") || "—"}</div>
                <div className="text-[10px] text-slate-400 mt-1">Created {new Date(k.created_at).toLocaleDateString()} · Last used: {k.last_used_at || "never"}</div>
              </div>
              <button onClick={() => revokeKey(k.id)} className="text-rose-600 hover:text-rose-700 text-xs inline-flex items-center gap-1" data-testid={`dev-key-revoke-${k.id}`}>
                <Trash2 className="w-3.5 h-3.5" /> Revoke
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Webhooks */}
      <section className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="dev-webhooks-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg text-slate-900 flex items-center gap-2"><WebhookIcon className="w-4 h-4 text-indigo-600" /> Webhooks</h2>
        </div>

        <form onSubmit={createHook} className="mb-6 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="dev-webhooks-create-form">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[280px]">
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Endpoint URL</label>
              <input
                required
                type="url"
                value={newHook.url}
                onChange={(e) => setNewHook({ ...newHook, url: e.target.value })}
                placeholder="https://your-app.com/cyannect/webhook"
                className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-slate-300 bg-white focus:border-indigo-500 outline-none"
                data-testid="dev-hook-url-input"
              />
            </div>
            <button
              type="submit" disabled={busy || newHook.event_types.length === 0}
              className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="dev-hook-create-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Create webhook
            </button>
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Event types (subscribe)</div>
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPES.map(t => (
                <button type="button" key={t} onClick={() => toggleEvent(t)}
                        className={`text-[11px] px-2.5 py-1 rounded-full font-mono transition ${newHook.event_types.includes(t) ? "bg-indigo-500 text-white" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}
                        data-testid={`dev-hook-event-${t}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </form>

        <div className="space-y-2" data-testid="dev-hooks-list">
          {hooks.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">No webhooks yet — subscribe to your first event above.</div>}
          {hooks.map(h => (
            <WebhookRow key={h.id} hook={h} onDelete={() => deleteHook(h.id)} />
          ))}
        </div>
      </section>

      {reveal && <RevealOnceModal {...reveal} onClose={() => setReveal(null)} testid="dev-reveal-modal" />}
    </div>
  );
}
